/**
 * Validates a post-sign-in return path came from this app's own routing,
 * never an attacker-supplied external target smuggled through the
 * ?redirect= query param (the classic open-redirect vector: a phishing
 * link to our own /sign-in page with redirect=https://evil.example or
 * redirect=//evil.example that would otherwise bounce a freshly
 * authenticated user off-site). Only a same-origin, single-leading-slash
 * path is accepted; anything else falls back to `fallback`.
 */
export function resolveSafeReturnPath(raw: string | null | undefined, fallback = '/'): string {
  if (!raw) return fallback;

  let candidate: string;
  try {
    candidate = decodeURIComponent(raw);
  } catch {
    return fallback;
  }

  // Reject absolute URLs (https://..., javascript:...) outright.
  if (candidate.includes('://') || candidate.includes(':\\')) return fallback;

  // Must start with exactly one "/" -- "//evil.example" and "/\evil.example"
  // are both browser-recognized protocol-relative forms some UAs will
  // still navigate off-origin, so both are rejected, not just "//".
  if (!/^\/(?!\/|\\)/.test(candidate)) return fallback;

  // No embedded whitespace/backslash beyond the leading check above --
  // keeps this a plain path+query+hash string.
  if (/[\s\\]/.test(candidate)) return fallback;

  return candidate;
}
